import { useEffect, useState } from "react";
import { Copy, CheckCircle2, Mail, Phone, Loader2, ShieldCheck, X } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, getErrorMessage } from "@/lib/api";

/* =================== Verify Contact Modal =================== */
function VerifyContactModal({ type, onClose, onVerified }) {
  const isEmail = type === "email";
  const [step, setStep] = useState(isEmail ? "send" : "enter_phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  async function sendCode() {
    setErr(""); setLoading(true);
    try {
      if (isEmail) {
        await api.post("/api/auth/email/resend-otp");
        setStep("verify");
        setCooldown(60);
      } else {
        await api.post("/api/auth/mobile/send-otp", { mobile_number: phone });
        setStep("verify");
        setCooldown(60);
      }
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setLoading(false); }
  }

  async function verify() {
    setErr(""); setLoading(true);
    try {
      if (isEmail) {
        await api.post("/api/auth/email/verify-otp", { code });
      } else {
        await api.post("/api/auth/mobile/verify-otp", { code });
      }
      onVerified();
      onClose();
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-[20px] border border-[var(--cm-border)] shadow-2xl max-w-[420px] w-full p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {isEmail ? <Mail size={20} className="text-cm-blue" /> : <Phone size={20} className="text-cm-blue" />}
            <h3 className="text-[16px] font-extrabold text-cm-navy">
              {isEmail ? "Verify Email" : "Verify Mobile"}
            </h3>
          </div>
          <button onClick={onClose} className="text-cm-grey hover:text-cm-navy p-1">
            <X size={20} />
          </button>
        </div>

        {step === "enter_phone" && (
          <div className="space-y-4">
            <p className="text-[13px] text-cm-grey">Enter your mobile number to receive a verification code.</p>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d+]/g, ""))}
              placeholder="+91 98765 43210"
              className="w-full px-4 py-3 rounded-[12px] border border-[#E0E3EF] text-[14px] outline-none focus:border-cm-blue focus:ring-2 focus:ring-cm-blue/10"
            />
            {err && <p className="text-[12px] text-red-600">{err}</p>}
            <button
              onClick={sendCode}
              disabled={loading || !phone}
              className="w-full py-3 rounded-[12px] bg-cm-blue text-white font-bold text-[14px] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Send OTP
            </button>
          </div>
        )}

        {step === "send" && isEmail && (
          <div className="space-y-4">
            <p className="text-[13px] text-cm-grey">
              We'll send a 6-digit verification code to your registered email address.
            </p>
            {err && <p className="text-[12px] text-red-600">{err}</p>}
            <button
              onClick={sendCode}
              disabled={loading}
              className="w-full py-3 rounded-[12px] bg-cm-blue text-white font-bold text-[14px] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Send verification code
            </button>
          </div>
        )}

        {step === "verify" && (
          <div className="space-y-4">
            <p className="text-[13px] text-cm-grey">
              Enter the 6-digit code sent to your {isEmail ? "email" : "mobile number"}.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="______"
              className="w-full px-4 py-3 rounded-[12px] border border-[#E0E3EF] text-[18px] font-mono text-center tracking-[0.4em] outline-none focus:border-cm-blue focus:ring-2 focus:ring-cm-blue/10"
            />
            {err && <p className="text-[12px] text-red-600">{err}</p>}
            <button
              onClick={verify}
              disabled={loading || code.length < 6}
              className="w-full py-3 rounded-[12px] bg-cm-blue text-white font-bold text-[14px] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 size={14} className="animate-spin" />}
              Verify
            </button>
            <button
              onClick={step === "verify" && !isEmail ? () => setStep("enter_phone") : sendCode}
              disabled={loading || cooldown > 0}
              className="w-full text-[13px] text-cm-blue font-semibold hover:underline disabled:opacity-50"
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* =================== Verification Banner =================== */
function VerificationBanner({ emailVerified, mobileVerified, onVerify }) {
  if (emailVerified && mobileVerified) return null;
  return (
    <div className="bg-amber-50 border border-amber-100 rounded-[16px] p-4 space-y-3">
      <h3 className="text-[13px] font-bold text-amber-800">Pending verifications</h3>
      {!emailVerified && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail size={15} className="text-amber-600" />
            <span className="text-[13px] text-amber-800">Email not verified</span>
          </div>
          <button
            onClick={() => onVerify("email")}
            className="text-[12px] font-bold text-cm-blue hover:underline px-3 py-1.5 rounded-[8px] bg-white border border-[#E0E3EF]"
          >
            Verify now
          </button>
        </div>
      )}
      {!mobileVerified && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone size={15} className="text-amber-600" />
            <span className="text-[13px] text-amber-800">Mobile not verified</span>
          </div>
          <button
            onClick={() => onVerify("mobile")}
            className="text-[12px] font-bold text-cm-blue hover:underline px-3 py-1.5 rounded-[8px] bg-white border border-[#E0E3EF]"
          >
            Verify now
          </button>
        </div>
      )}
    </div>
  );
}

export default function ProfilePage() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [verifyModal, setVerifyModal] = useState(null); // "email" | "mobile" | null
  const [nameDraft, setNameDraft] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [nameBusy, setNameBusy] = useState(false);
  const [nameErr, setNameErr] = useState("");
  const [nameOk, setNameOk] = useState("");

  async function loadProfile() {
    const r = await api.get("/api/user/overview");
    setData(r.data);
    const existing =
      r.data?.user?.full_name ||
      r.data?.kyc_profile?.full_name ||
      "";
    setNameDraft(existing === "-" || existing === "—" ? "" : existing);
  }

  useEffect(() => { loadProfile(); }, []);

  function handleVerified() {
    loadProfile();
  }

  const displayName =
    data?.user?.full_name ||
    data?.kyc_profile?.full_name ||
    "";
  const needsName =
    !displayName ||
    displayName === "-" ||
    displayName === "—";

  async function saveName() {
    setNameErr("");
    setNameOk("");
    const name = nameDraft.trim().replace(/\s+/g, " ");
    if (name.length < 2 || name === "-") {
      setNameErr("Please enter your full name");
      return;
    }
    setNameBusy(true);
    try {
      await api.patch("/api/auth/profile", { full_name: name });
      setEditingName(false);
      setNameOk("Name updated.");
      await loadProfile();
    } catch (e) {
      setNameErr(getErrorMessage(e));
    } finally {
      setNameBusy(false);
    }
  }

  return (
    <AppShell>
      {verifyModal && (
        <VerifyContactModal
          type={verifyModal}
          onClose={() => setVerifyModal(null)}
          onVerified={handleVerified}
        />
      )}

      <div className="space-y-6">
        <h1 className="text-[26px] font-bold text-cm-navy tracking-tight">Profile</h1>

        {!data ? (
          <div className="text-cm-grey">Loading…</div>
        ) : (
          <>
            <VerificationBanner
              emailVerified={data.user?.email_verified}
              mobileVerified={data.user?.mobile_verified}
              onVerify={(type) => setVerifyModal(type)}
            />

            {needsName && !editingName ? (
              <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900 flex flex-wrap items-center justify-between gap-3">
                <span>Your profile is missing a full name. Add it so Digio KYC and Travel Rule can use the correct identity.</span>
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  className="px-3 py-1.5 rounded-[10px] bg-cm-blue text-white text-[12px] font-semibold"
                >
                  Add name
                </button>
              </div>
            ) : null}

            <div className="bg-white rounded-[16px] border border-[var(--cm-border)] p-6 space-y-5">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
                <div className="col-span-2 sm:col-span-1">
                  <div className="text-cm-grey text-[11px] uppercase tracking-wider">Full name</div>
                  {editingName ? (
                    <div className="mt-1 space-y-2">
                      <input
                        type="text"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        placeholder="As on your government ID"
                        className="w-full px-3 py-2 rounded-[10px] border border-[#E0E3EF] text-[14px] outline-none focus:border-cm-blue"
                        data-testid="profile-full-name"
                      />
                      {nameErr ? <p className="text-[12px] text-red-600">{nameErr}</p> : null}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={nameBusy}
                          onClick={saveName}
                          className="px-3 py-1.5 rounded-[10px] bg-cm-blue text-white text-[12px] font-semibold disabled:opacity-60 inline-flex items-center gap-1"
                        >
                          {nameBusy ? <Loader2 size={12} className="animate-spin" /> : null}
                          Save
                        </button>
                        <button
                          type="button"
                          disabled={nameBusy}
                          onClick={() => {
                            setEditingName(false);
                            setNameErr("");
                            setNameDraft(
                              displayName === "-" || displayName === "—" ? "" : displayName
                            );
                          }}
                          className="px-3 py-1.5 rounded-[10px] border border-[var(--cm-border)] text-[12px] font-semibold text-cm-grey"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-cm-navy font-medium flex items-center gap-2 mt-0.5">
                      <span>{needsName ? "—" : displayName}</span>
                      <button
                        type="button"
                        onClick={() => setEditingName(true)}
                        className="text-[12px] font-semibold text-cm-blue hover:underline"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                  {nameOk && !editingName ? (
                    <p className="text-[12px] text-emerald-600 mt-1">{nameOk}</p>
                  ) : null}
                </div>
                {[
                  ["Email", data.user.email],
                  ["Mobile", data.user.mobile_number || data.kyc_profile?.mobile_number || "—"],
                  ["Account ID", data.user.id],
                  ["Role", data.user.role || "user"],
                  ["Member since", data.user.created_at?.slice(0, 10)],
                  ["KYC status", data.user.kyc_status],
                ].map(([k, v]) => (
                  <div key={k}>
                    <div className="text-cm-grey text-[11px] uppercase tracking-wider">{k}</div>
                    <div className="text-cm-navy font-medium flex items-center gap-2">
                      {v || "—"}
                      {k === "Account ID" && (
                        <button
                          onClick={async () => {
                            await navigator.clipboard.writeText(data.user.id);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1200);
                          }}
                          className="text-cm-grey hover:text-cm-blue"
                          aria-label="Copy account id"
                        >
                          {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Verification status row */}
              <div className="pt-4 border-t border-[var(--cm-border)]">
                <p className="text-[11px] font-semibold text-cm-grey uppercase tracking-wider mb-3">Verification status</p>
                <div className="flex flex-wrap gap-3">
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-[10px] text-[12px] font-semibold border ${
                    data.user?.email_verified
                      ? "bg-green-50 border-green-200 text-green-700"
                      : "bg-amber-50 border-amber-200 text-amber-700"
                  }`}>
                    <Mail size={13} />
                    Email: {data.user?.email_verified ? "Verified" : "Not verified"}
                    {data.user?.email_verified && <ShieldCheck size={12} />}
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-[10px] text-[12px] font-semibold border ${
                    data.user?.mobile_verified
                      ? "bg-green-50 border-green-200 text-green-700"
                      : "bg-amber-50 border-amber-200 text-amber-700"
                  }`}>
                    <Phone size={13} />
                    Mobile: {data.user?.mobile_verified ? "Verified" : "Not verified"}
                    {data.user?.mobile_verified && <ShieldCheck size={12} />}
                  </div>

                  {!data.user?.email_verified && (
                    <button
                      onClick={() => setVerifyModal("email")}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-[12px] font-semibold bg-cm-blue text-white hover:bg-cm-blue-deep transition"
                    >
                      <Mail size={13} /> Verify email
                    </button>
                  )}
                  {!data.user?.mobile_verified && (
                    <button
                      onClick={() => setVerifyModal("mobile")}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-[10px] text-[12px] font-semibold bg-cm-blue text-white hover:bg-cm-blue-deep transition"
                    >
                      <Phone size={13} /> Verify mobile
                    </button>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
